import { useState, useEffect, useCallback, useRef } from "react";
import { companyService, type CompanyDto } from "../services/companyService";
import { userService, type UserResponse } from "../services/userService";
import { useAuth } from "./useAuth";
import { useCompanyContext } from "../contexts/CompanyContext";

// Local storage keys
const COMPANIES_CACHE_KEY = "companies_cache";

interface CompaniesCache {
  companies: CompanyDto[];
  userCompany: CompanyDto | null;
  userProfile: UserResponse | null;
  userId: string;
}

export interface CompanyContextData {
  companies: import("../services/companyService").CompanyDto[];
  userCompany: import("../services/companyService").CompanyDto | null;
  isLoading: boolean;
  error: string | null;
  availableCompanies: import("../services/companyService").CompanyDto[]; // Companies available for selection
  refreshCompanies: () => Promise<void>;
  setSelectedCompany: (companyId: string) => void;
  selectedCompanyId: string | null;
  isSuperAdmin: boolean; // True if user has access to all companies
  userRoleName: string | null; // User's role name
}

export const useCompany = (): CompanyContextData => {
  const context = useCompanyContext();
  
  return {
    companies: context.companies,
    userCompany: context.userCompany,
    isLoading: context.isLoading,
    error: context.error,
    availableCompanies: context.availableCompanies,
    refreshCompanies: context.refreshCompanies,
    setSelectedCompany: context.setSelectedCompany,
    selectedCompanyId: context.selectedCompanyId,
    isSuperAdmin: context.isSuperAdmin,
    userRoleName: context.userRoleName,
  };
};

// Helper functions for local storage
const getCachedCompanies = (userId: string): CompaniesCache | null => {
  try {
    const cached = localStorage.getItem(COMPANIES_CACHE_KEY);
    if (!cached) return null;

    const data: CompaniesCache = JSON.parse(cached);

    // Validate cache: check if it's for the same user
    if (data.userId !== userId) {
      localStorage.removeItem(COMPANIES_CACHE_KEY);
      return null;
    }

    return data;
  } catch (error) {
    console.error("Error reading companies cache:", error);
    localStorage.removeItem(COMPANIES_CACHE_KEY);
    return null;
  }
};

const setCachedCompanies = (
  companies: CompanyDto[],
  userCompany: CompanyDto | null,
  userProfile: UserResponse | null,
  userId: string
): void => {
  try {
    const cache: CompaniesCache = {
      companies,
      userCompany,
      userProfile,
      userId,
    };
    localStorage.setItem(COMPANIES_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.error("Error saving companies cache:", error);
  }
};

const clearCompaniesCache = (): void => {
  try {
    localStorage.removeItem(COMPANIES_CACHE_KEY);
  } catch (error) {
    console.error("Error clearing companies cache:", error);
  }
};


